import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, type User } from 'firebase/auth';
import { Loader2, LogOut } from 'lucide-react';
import { auth } from '../../services/firebase';
import { meAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SettingsPanel } from './SettingsPanel';

const PASSWORD_ERRORS: Record<string, string> = {
  'auth/invalid-credential': 'Your current password is not right.',
  'auth/wrong-password': 'Your current password is not right.',
  'auth/weak-password': 'Use at least 6 characters for the new password.',
  'auth/too-many-requests': 'Too many tries. Wait a few minutes and try again.',
};

export function ProfileSettings() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState(auth?.currentUser?.email ?? '');

  useEffect(() => {
    meAPI.get().then((me) => {
      setName(me.user.displayName ?? '');
      setEmail(me.user.email);
    }).catch(() => {});
  }, []);

  const user = auth?.currentUser as User | null | undefined;
  const usesPassword = !!user?.providerData.some((p) => p.providerId === 'password');

  return (
    <div className="grid gap-4">
      <SettingsPanel title="Profile">
        <Field label="Display name"><Input aria-label="Display name" value={name} readOnly /></Field>
        <Field label="Email"><Input aria-label="Email" value={email} readOnly /></Field>
      </SettingsPanel>
      <SettingsPanel title="Change password">
        {usesPassword
          ? <PasswordForm />
          : <p className="text-sm text-muted-foreground">You sign in with Google, so your password is managed in your Google account.</p>}
      </SettingsPanel>
      <SignOutPanel />
    </div>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (next !== confirm) {
      setError('The new passwords do not match.');
      return;
    }
    const user = auth?.currentUser as User | null | undefined;
    if (!user?.email) {
      setError('Sign in again to change your password.');
      return;
    }
    setSaving(true);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, current));
      await updatePassword(user, next);
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError(PASSWORD_ERRORS[(err as { code?: string })?.code ?? ''] ?? 'Could not change your password. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-3">
      <Field label="Current password">
        <Input aria-label="Current password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
      </Field>
      <Field label="New password">
        <Input aria-label="New password" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} />
      </Field>
      <Field label="Confirm new password">
        <Input aria-label="Confirm new password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
      </Field>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {done && <p className="text-sm text-primary">Password changed. Your other devices will be signed out within the hour.</p>}
      <div>
        <Button type="submit" size="sm" disabled={saving}>
          {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          Change password
        </Button>
      </div>
    </form>
  );
}

function SignOutPanel() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <SettingsPanel title="Sign out">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Signs you out on this device. Changing your password signs you out everywhere else.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            await signOut();
            navigate({ to: '/login' });
          }}
        >
          <LogOut className="mr-1.5 h-3.5 w-3.5" /> Sign out
        </Button>
      </div>
    </SettingsPanel>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
